import { Box, FormControl, FormLabel, Text } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { Select } from '~/packages/common-frontend/components/Select';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { useGetPipelineRunsWithArtifactIdsQuery } from '~/packages/dataPipeline/requests/dataPipeline.gql.generated';

export type RDFV = {
  reportingDate: Util.FullDateStr;
  fileVersion: number;
};

type DateVersionPickerProps = {
  organizationId: string;
  minDate: Date;
  maxDate: Date;
  initialDate: Util.FullDateStr;
  /** The artifact being uploaded - used to filter out versions that already contain this artifact */
  artifactId: string;
  /** If true, default to new version; otherwise default to highest available existing version */
  defaultToNewVersion: boolean;
  onSelect: (rdfv: RDFV) => void;
};

/**
 * DateVersionPicker allows users to:
 * 1. Select a reporting date
 * 2. Choose an existing version to add files to (if the artifact isn't already in it), OR create a new version
 *
 * If no existing versions exist for the selected date, it defaults to creating version 1.
 * Existing versions that already contain the artifact are filtered out.
 */
export const DateVersionPicker = ({
  organizationId,
  minDate,
  maxDate,
  initialDate,
  artifactId,
  defaultToNewVersion,
  onSelect,
}: DateVersionPickerProps) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [selectedDate, setSelectedDate] = useState<Util.FullDateStr>(
    initialDate,
  );
  const [selectedVersion, setSelectedVersion] = useState<number>(1);

  // Fetch pipeline runs for the selected date with artifact IDs
  const {
    data: pipelineRunsData,
    loading,
  } = useGetPipelineRunsWithArtifactIdsQuery({
    variables: {
      borrowerOrganizationId: organizationId,
      startDate: selectedDate,
      endDate: selectedDate,
    },
    fetchPolicy: 'network-only',
  });

  // Get all versions for the selected date, filtering out versions that already contain this artifact
  const { availableVersions, newVersion } = useMemo(() => {
    if (!pipelineRunsData?.getPipelineRunsWithArtifactIds) {
      return { availableVersions: [], newVersion: 1 };
    }

    const runsForDate = pipelineRunsData.getPipelineRunsWithArtifactIds.filter(
      (run) => run.reportingDate === selectedDate,
    );

    // Find max version across all runs (needed to calculate next version)
    const maxVersion =
      runsForDate.length > 0
        ? Math.max(...runsForDate.map((run) => run.fileVersion))
        : 0;

    // Filter out versions that already contain this artifact
    const available = runsForDate
      .filter((run) => !run.uploadedArtifactIds.includes(artifactId))
      .map((run) => run.fileVersion)
      .sort((a, b) => b - a); // Sort descending (newest first)

    return { availableVersions: available, newVersion: maxVersion + 1 };
  }, [pipelineRunsData, selectedDate, artifactId]);

  // Build version options: existing versions + new version at the top
  const versionOptions = useMemo(() => {
    const options = [
      { label: `(New) Version ${newVersion}`, value: newVersion },
      ...availableVersions.map((version) => ({
        label: `Version ${version}`,
        value: version,
      })),
    ];
    return options;
  }, [availableVersions, newVersion]);

  // When date changes or data loads, select appropriate default version
  // - If defaultToNewVersion or no available existing versions: select new version
  // - Otherwise: select the highest available existing version
  useEffect(() => {
    if (defaultToNewVersion || availableVersions.length === 0) {
      setSelectedVersion(newVersion);
    } else {
      // availableVersions is sorted descending, so [0] is the highest
      setSelectedVersion(availableVersions[0]);
    }
  }, [defaultToNewVersion, availableVersions, newVersion, selectedDate]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelect({ reportingDate: selectedDate, fileVersion: selectedVersion });
  }, [selectedDate, selectedVersion, onSelect]);

  return (
    <Box>
      <SimpleDatePicker
        tz={clientTimeZone}
        dateFormat="MMMM dd, yyyy"
        minDate={minDate}
        maxDate={maxDate}
        formControlProps={{ pb: '0.5rem' }}
        label="Select reporting date for file:"
        selectedDate={commonDate.fullDateStrToDate(selectedDate)}
        showTimeSelect={false}
        setSelectedDate={(date: Date | null) => {
          if (!date) return;
          const fullDateStr = commonDate.dateToFullDateString(
            date,
            clientTimeZone,
          );
          setSelectedDate(fullDateStr);
        }}
        width="100%"
      />

      <FormControl mb="1rem">
        <FormLabel fontSize="sm" fontWeight="medium">
          Select version:
        </FormLabel>
        {loading ? (
          <Text fontSize="sm" color="gray.500">
            Loading versions...
          </Text>
        ) : (
          <Select<number>
            value={selectedVersion}
            options={versionOptions}
            onChange={(value) => {
              if (typeof value === 'number') {
                setSelectedVersion(value);
              }
            }}
          />
        )}
      </FormControl>
    </Box>
  );
};
